<template>
    <v-hover v-if="collapsed" v-slot="{ hover }">
        <v-list-item class="justify-center px-0" :class="[{ 'grey lighten-4': hover }, { pointer: canOpenStudentQuotas }]" @click="openStudentQuotas">
            <v-list-item-action class="mx-auto">
                <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                        <div class="d-flex" v-bind="attrs" v-on="on">
                            <v-progress-circular
                                v-if="!fetchingInstanceUsage && !$isError(instanceUsage) && !isMasterInstance"
                                :rotate="-90"
                                :size="36"
                                :width="4"
                                :value="usagePercent"
                                :color="usagePercent <= 90 ? 'primary' : 'error'">
                                <span class="caption">{{ Math.floor(usagePercent) }}</span>
                            </v-progress-circular>
                            <v-progress-circular v-else-if="fetchingInstanceUsage" indeterminate :size="36" :width="4" color="primary" />
                            <v-icon v-else>mdi-credit-card-outline</v-icon>
                        </div>
                    </template>
                    <span>{{ usageTooltipText }}</span>
                </v-tooltip>
            </v-list-item-action>
        </v-list-item>
    </v-hover>
    <v-hover v-else v-slot="{ hover }">
        <div :class="{ pointer: canOpenStudentQuotas }" @click="openStudentQuotas">
            <v-card :class="{ 'grey lighten-4': hover }" elevation="0">
                <v-card-text>
                    <dashboard-loader v-if="fetchingInstanceUsage"></dashboard-loader>
                    <template v-else>
                        <template v-if="$isError(instanceUsage)">
                            <div class="caption font-weight-medium d-flex align-center">
                                Credit quota:&nbsp;
                                <b>N/A</b>
                            </div>
                            <div class="caption font-weight-medium d-flex align-center">
                                Ends on:&nbsp;
                                <b>N/A</b>
                            </div>
                        </template>
                        <template v-else>
                            <div v-if="!isMasterInstance">
                                <v-progress-linear
                                    rounded
                                    height="6"
                                    :value="usagePercent"
                                    :color="usagePercent <= 90 ? 'primary' : 'error'"
                                    class="mb-2"></v-progress-linear>
                                <div class="caption font-weight-medium d-flex align-center">
                                    Credit quota:&nbsp;
                                    <b>{{ instanceUsage.used || 0 }}</b>
                                    &nbsp;of&nbsp;
                                    <b>{{ instanceUsage.limit }}</b>
                                    &nbsp;used
                                </div>
                            </div>
                            <div v-else>
                                <div class="caption font-weight-medium d-flex align-center">
                                    Credit quota:&nbsp;
                                    <b>{{ instanceUsage.limit }}</b>
                                    <v-icon v-if="canOpenStudentQuotas" right small>mdi-open-in-new</v-icon>
                                </div>
                            </div>
                            <div class="caption font-weight-medium d-flex align-center">
                                Ends on:&nbsp;
                                <b>{{ instanceUsage.end_date }}</b>
                            </div>
                        </template>
                    </template>
                </v-card-text>
            </v-card>
        </div>
    </v-hover>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import { humanFileSize } from '@/utils'
import { mapGetters, mapState } from 'vuex'

const DashboardLoader = () => import('@/components/DashboardLoader.vue')

export default {
    name: 'InstanceUsageCard',
    mixins: [enumsData],
    components: { DashboardLoader },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {}
    },
    created() {
        this.$store.dispatch('instanceStore/fetchInstanceUsage', this.$route.params.iid)
    },
    methods: {
        humanFileSize(bytes, si) {
            return humanFileSize(bytes, si)
        },
        openStudentQuotas() {
            if (!this.canOpenStudentQuotas) {
                return
            }

            this.$router.push({
                name: 'config-space-quotas',
                params: {
                    oid: this.$route.params.oid,
                    sid: this.$route.params.sid
                }
            })
        }
    },
    computed: {
        ...mapState('instanceStore', ['instanceUsage', 'fetchingInstanceUsage']),
        ...mapGetters('instanceStore', ['isMasterInstance']),
        ...mapGetters('spaceStore', ['currentSpaceType', 'isSpaceAdmin']),
        isEducationSpace() {
            return this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE
        },
        canOpenStudentQuotas() {
            return this.isEducationSpace && this.isSpaceAdmin
        },
        usageTooltipText() {
            if (this.fetchingInstanceUsage) {
                return 'Loading credit quota usage...'
            }

            if (this.$isError(this.instanceUsage)) {
                return 'Credit quota: N/A. Ends on: N/A.'
            }

            if (!this.isMasterInstance) {
                return `Credit quota: ${this.instanceUsage?.used || 0} of ${this.instanceUsage?.limit || 0} used. Ends on: ${
                    this.instanceUsage?.end_date || 'N/A'
                }.`
            }

            return `Credit quota: ${this.instanceUsage?.limit || 0}. Ends on: ${this.instanceUsage?.end_date || 'N/A'}.`
        },
        usagePercent() {
            return this.instanceUsage ? (this.instanceUsage.used / this.instanceUsage.limit) * 100 : 0
        }
    }
}
</script>
